import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { X, ShoppingBag, Trash2, Plus, Minus, ArrowRight, Sparkles } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const EASE = [0.22, 1, 0.36, 1];

export default function CartDrawer() {
  const { cart, drawerOpen, closeCartDrawer, removeFromCart, updateCartItem } = useCart();
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const id = language === 'id';

  const items = cart?.items || [];
  const subtotal = items.reduce((sum, item) => {
    const price = item.product?.price || 0;
    const addonTotal = item.addons?.reduce((a, addon) => a + (addon.price || 0), 0) || 0;
    return sum + (price + addonTotal) * (item.quantity || 1);
  }, 0);

  const fmt = (p) => new Intl.NumberFormat('id-ID').format(p);

  const goCheckout = () => {
    closeCartDrawer();
    if (!user) {
      navigate('/login');
    } else {
      navigate('/checkout');
    }
  };

  const continueShopping = () => {
    closeCartDrawer();
    navigate('/catalog');
  };

  const resolveImg = (raw) => {
    if (!raw) return 'https://images.unsplash.com/photo-1551468220-0a25172193f9?w=400&q=80';
    if (raw.startsWith('http')) return raw;
    return `${BACKEND}${raw}`;
  };

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="cart-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            onClick={closeCartDrawer}
            className="fixed inset-0 bg-seikat-ink/70 backdrop-blur-sm z-[80]"
            data-testid="cart-drawer-backdrop"
          />

          {/* Drawer */}
          <motion.aside
            key="cart-drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.55, ease: EASE }}
            className="fixed top-0 right-0 bottom-0 z-[90] w-full sm:w-[440px] md:w-[480px] dark-immersive flex flex-col border-l border-white/8 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.7)]"
            data-testid="cart-drawer"
            style={{ background: '#120E0D' }}
          >
            {/* Header */}
            <header className="flex items-center justify-between px-7 py-6 border-b border-white/8">
              <div>
                <p className="eyebrow-immersive text-seikat-lime">{id ? 'Keranjangmu' : 'Your Cart'}</p>
                <h2 className="font-heading text-3xl text-seikat-bone mt-1">
                  {items.length} {id ? (items.length === 1 ? 'tangkai' : 'tangkai') : (items.length === 1 ? 'item' : 'items')}
                </h2>
              </div>
              <button
                onClick={closeCartDrawer}
                className="w-10 h-10 rounded-full glass-dark flex items-center justify-center text-seikat-bone hover:text-seikat-pink transition-colors"
                data-testid="cart-drawer-close"
                aria-label="Close cart"
              >
                <X size={18} />
              </button>
            </header>

            {/* Items list */}
            <div className="flex-1 overflow-y-auto px-7 py-6">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-16" data-testid="cart-drawer-empty">
                  <div className="w-20 h-20 rounded-full glass-dark flex items-center justify-center mb-6">
                    <ShoppingBag size={28} className="text-seikat-pink/70" strokeWidth={1.4} />
                  </div>
                  <h3 className="font-heading text-2xl text-seikat-bone">
                    {id ? 'Belum ada bunga di sini' : 'No flowers yet'}
                  </h3>
                  <p className="font-body text-seikat-bone/60 text-sm mt-2 max-w-xs">
                    {id ? 'Jelajahi koleksi atau rangkai memory bouquet pertamamu.' : 'Browse the catalog or weave your first memory bouquet.'}
                  </p>
                  <div className="flex flex-col gap-3 mt-8 w-full max-w-xs">
                    <button onClick={continueShopping} className="btn-pill btn-pill--solid w-full justify-center" data-testid="cart-drawer-empty-browse">
                      {id ? 'Jelajahi Bouquet' : 'Explore Bouquets'} <ArrowRight size={14} />
                    </button>
                    <button onClick={() => { closeCartDrawer(); navigate('/memory-bouquet'); }} className="btn-pill w-full justify-center">
                      <Sparkles size={14} /> {id ? 'Memory Bouquet' : 'Memory Bouquet'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((item) => {
                    const product = item.product || {};
                    const name = id ? product.name_id : product.name_en;
                    const img = resolveImg(product.images?.[0] || product.image_url);
                    return (
                      <motion.div
                        key={item.item_id}
                        layout
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: 60 }}
                        transition={{ duration: 0.35, ease: EASE }}
                        className="flex gap-4 glass-dark rounded-2xl p-4"
                        data-testid={`cart-drawer-item-${item.item_id}`}
                      >
                        <img src={img} alt={name} className="w-20 h-24 object-cover rounded-lg flex-shrink-0" />
                        <div className="flex-1 min-w-0 flex flex-col">
                          <h4 className="font-heading text-lg text-seikat-bone leading-tight line-clamp-1">{name || 'Untitled'}</h4>
                          <p className="font-body text-xs text-seikat-bone/60 mt-0.5">Rp {fmt(product.price || 0)}</p>
                          {item.addons?.length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {item.addons.map((addon, i) => (
                                <span key={i} className="text-[10px] px-2 py-0.5 bg-seikat-pink/15 text-seikat-pink rounded-full capitalize">
                                  {addon.type?.replace(/_/g, ' ')}
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="mt-auto pt-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => updateCartItem(item.item_id, { quantity: Math.max(1, (item.quantity || 1) - 1) })}
                                className="w-7 h-7 rounded-full border border-white/15 text-seikat-bone/70 hover:text-seikat-pink hover:border-seikat-pink/40 transition-colors flex items-center justify-center"
                                data-testid={`cart-drawer-decr-${item.item_id}`}
                              >
                                <Minus size={11} />
                              </button>
                              <span className="font-body text-sm text-seikat-bone w-6 text-center">{item.quantity || 1}</span>
                              <button
                                onClick={() => updateCartItem(item.item_id, { quantity: (item.quantity || 1) + 1 })}
                                className="w-7 h-7 rounded-full border border-white/15 text-seikat-bone/70 hover:text-seikat-pink hover:border-seikat-pink/40 transition-colors flex items-center justify-center"
                                data-testid={`cart-drawer-incr-${item.item_id}`}
                              >
                                <Plus size={11} />
                              </button>
                            </div>
                            <button
                              onClick={() => removeFromCart(item.item_id)}
                              className="text-seikat-bone-muted hover:text-seikat-pink transition-colors"
                              data-testid={`cart-drawer-remove-${item.item_id}`}
                              aria-label="Remove item"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer summary + CTA */}
            {items.length > 0 && (
              <footer className="border-t border-white/8 px-7 py-6 space-y-4">
                <div className="flex items-baseline justify-between">
                  <span className="eyebrow-immersive">{id ? 'Subtotal' : 'Subtotal'}</span>
                  <span className="font-heading text-3xl text-seikat-bone">Rp {fmt(subtotal)}</span>
                </div>
                <p className="font-body text-[11px] text-seikat-bone-muted">
                  {id ? 'Ongkir dihitung di checkout.' : 'Shipping calculated at checkout.'}
                </p>
                <button
                  onClick={goCheckout}
                  className="btn-pill btn-pill--solid w-full justify-center"
                  data-testid="cart-drawer-checkout"
                >
                  {id ? 'Lanjutkan ke Checkout' : 'Proceed to Checkout'} <ArrowRight size={14} />
                </button>
                <button
                  onClick={continueShopping}
                  className="w-full text-center text-xs tracking-[0.25em] uppercase text-seikat-bone-muted hover:text-seikat-pink transition-colors py-2"
                  data-testid="cart-drawer-continue"
                >
                  {id ? 'Lanjutkan Belanja' : 'Continue Shopping'}
                </button>
              </footer>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
